/**
 * Deep clone utility function that properly handles various data types
 * More robust than JSON.parse(JSON.stringify()) and handles circular references
 */
export function deepClone<T>(obj: T): T {
  return deepCloneInternal(obj, new WeakMap());
}

/**
 * Internal deep clone implementation with circular reference detection
 */
function deepCloneInternal<T>(obj: T, cloneMap: WeakMap<object, any>): T {
  // Handle null, undefined, and primitive types
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Check for circular reference
  if (cloneMap.has(obj as object)) {
    return cloneMap.get(obj as object);
  }

  // Handle Date
  if (obj instanceof Date) {
    const cloned = new Date(obj.getTime()) as any;
    cloneMap.set(obj as object, cloned);
    return cloned;
  }

  // Handle Array
  if (Array.isArray(obj)) {
    const clonedArr: any[] = [];
    cloneMap.set(obj as object, clonedArr);
    for (let i = 0; i < obj.length; i++) {
      clonedArr[i] = deepCloneInternal(obj[i], cloneMap);
    }
    return clonedArr as any;
  }

  // Handle RegExp
  if (obj instanceof RegExp) {
    const cloned = new RegExp(obj.source, obj.flags) as any;
    cloneMap.set(obj as object, cloned);
    return cloned;
  }

  // Handle Map
  if (obj instanceof Map) {
    const clonedMap = new Map();
    cloneMap.set(obj as object, clonedMap);
    obj.forEach((value, key) => {
      clonedMap.set(deepCloneInternal(key, cloneMap), deepCloneInternal(value, cloneMap));
    });
    return clonedMap as any;
  }

  // Handle Set
  if (obj instanceof Set) {
    const clonedSet = new Set();
    cloneMap.set(obj as object, clonedSet);
    obj.forEach((value) => {
      clonedSet.add(deepCloneInternal(value, cloneMap));
    });
    return clonedSet as any;
  }

  // Handle Object
  const clonedObj: any = {};
  cloneMap.set(obj as object, clonedObj);
  
  // Get all property names (including non-enumerable) and symbol keys
  const stringProps = Object.getOwnPropertyNames(obj);
  const symbolProps = Object.getOwnPropertySymbols(obj);
  const allProps = [...stringProps, ...symbolProps];
  
  for (const prop of allProps) {
    const descriptor = Object.getOwnPropertyDescriptor(obj, prop);
    if (descriptor) {
      // For non-configurable properties, we can still clone the value but make it configurable in the clone
      const clonedDescriptor = { ...descriptor };
      
      // Clone the value if it exists
      if (descriptor.value !== undefined) {
        clonedDescriptor.value = deepCloneInternal(descriptor.value, cloneMap);
      }
      
      // Clone getter and setter functions if they exist
      if (descriptor.get) {
        clonedDescriptor.get = deepCloneInternal(descriptor.get, cloneMap);
      }
      if (descriptor.set) {
        clonedDescriptor.set = deepCloneInternal(descriptor.set, cloneMap);
      }
      
      // For non-configurable properties, make them configurable in the clone to avoid issues
      if (!descriptor.configurable) {
        clonedDescriptor.configurable = true;
      }
      
      // Define the property on the cloned object
      try {
        Object.defineProperty(clonedObj, prop, clonedDescriptor);
      } catch (error) {
        // If defineProperty fails, try a simple assignment for the value
        if (clonedDescriptor.value !== undefined) {
          clonedObj[prop] = clonedDescriptor.value;
        }
      }
    }
  }

  // Preserve prototype chain
  Object.setPrototypeOf(clonedObj, Object.getPrototypeOf(obj));
  
  return clonedObj;
}

/**
 * Type-safe deep clone specifically for navigation items
 * Handles the TreoNavigationItem structure
 */
export function cloneNavigationItems<T extends any[]>(items: T): T {
  return deepClone(items);
}